import React, { useState, useEffect, useCallback } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { PageLayout, PageHeader } from '../../components'
import { StatusChip, LoadingSpinner } from '../../components/atoms'
import { authenticatedFetchJson } from '../../services/api/apiClient'
import { apiEndpoints } from '../../consts'
import logger from '../../utils/logger'
import styles from './AdminBillingLogs.module.scss'

const TABS = [
  { id: 'jobs', label: 'Jobs', icon: 'clock', retention: '30 days' },
  { id: 'autoqa', label: 'Auto QA', icon: 'check-circle', retention: '90 days' },
  { id: 'emails', label: 'Emails', icon: 'envelope', retention: '90 days' },
  { id: 'webhooks', label: 'Webhooks', icon: 'bolt', retention: '90 days' },
  { id: 'audit', label: 'Audit Log', icon: 'file-alt', retention: 'Kept forever' },
  { id: 'auth', label: 'Auth (Shadow)', icon: 'user-check', retention: '90 days' }
]

const STATUS_COLORS = {
  success: 'success',
  sent: 'success',
  processed: 'success',
  completed: 'success',
  failed: 'error',
  error: 'error',
  running: 'warning',
  pending: 'warning',
  bounced: 'error',
  would_allow: 'success',
  would_deny: 'error'
}

const PAGE_SIZE = 50

const formatDate = (dateStr) => {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

const formatDuration = (ms) => {
  if (ms == null) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  return `${(ms / 60000).toFixed(1)}m`
}

const JobsTable = ({ rows }) => (
  <table className={styles.table}>
    <thead>
      <tr>
        <th>Job</th>
        <th>Status</th>
        <th>Started</th>
        <th>Duration</th>
        <th>Steps</th>
        <th>Error</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id}>
          <td className={styles.jobName}>{r.job_name}</td>
          <td>
            <StatusChip
              label={r.status}
              color={STATUS_COLORS[r.status] || 'neutral'}
              size='small'
            />
          </td>
          <td>{formatDate(r.started_at)}</td>
          <td>{formatDuration(r.duration_ms)}</td>
          <td className={styles.stepsCell}>{r.steps ? Object.keys(r.steps).length : 0}</td>
          <td className={styles.errorCell}>{r.error || ''}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const AutoQaTable = ({ rows }) => (
  <table className={styles.table}>
    <thead>
      <tr>
        <th>Batch Date</th>
        <th>Status</th>
        <th>Started</th>
        <th>Calls</th>
        <th>Passed</th>
        <th>Failed</th>
        <th>Error</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id}>
          <td>{r.batch_date}</td>
          <td>
            <StatusChip
              label={r.status}
              color={STATUS_COLORS[r.status] || 'neutral'}
              size='small'
            />
          </td>
          <td>{formatDate(r.started_at)}</td>
          <td>
            {r.processed_calls} / {r.total_calls}
          </td>
          <td>{r.passed_calls}</td>
          <td>{r.failed_calls + (r.error_calls || 0)}</td>
          <td className={styles.errorCell}>{r.error_message || ''}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const EmailsTable = ({ rows }) => (
  <table className={styles.table}>
    <thead>
      <tr>
        <th>Template</th>
        <th>Invoice</th>
        <th>Action</th>
        <th>Status</th>
        <th>Recipients</th>
        <th>Sent</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => {
        const recipients = Array.isArray(r.recipients) ? r.recipients : []
        return (
          <tr key={r.id}>
            <td className={styles.jobName}>
              {r.template_key || `#${r.billing_email_template_id}`}
            </td>
            <td>{r.invoice_id ? `#${r.invoice_id}` : '—'}</td>
            <td>{r.action}</td>
            <td>
              <StatusChip
                label={r.status}
                color={STATUS_COLORS[r.status] || 'neutral'}
                size='small'
              />
            </td>
            <td className={styles.recipientsCell}>
              {recipients.map((x) => x.email).join(', ') || '—'}
            </td>
            <td>{formatDate(r.sent_at)}</td>
          </tr>
        )
      })}
    </tbody>
  </table>
)

const WebhooksTable = ({ rows }) => (
  <table className={styles.table}>
    <thead>
      <tr>
        <th>Event Type</th>
        <th>Status</th>
        <th>Stripe ID</th>
        <th>Created</th>
        <th>Processed</th>
        <th>Error</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id}>
          <td className={styles.jobName}>{r.event_type}</td>
          <td>
            <StatusChip
              label={r.status}
              color={STATUS_COLORS[r.status] || 'neutral'}
              size='small'
            />
          </td>
          <td className={styles.monoCell}>{r.stripe_event_id}</td>
          <td>{formatDate(r.created_at)}</td>
          <td>{formatDate(r.processed_at)}</td>
          <td className={styles.errorCell}>{r.error_message || ''}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const AuditTable = ({ rows }) => (
  <table className={styles.table}>
    <thead>
      <tr>
        <th>Invoice</th>
        <th>Action</th>
        <th>Performed By</th>
        <th>Reason</th>
        <th>When</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id}>
          <td>#{r.invoice_id}</td>
          <td className={styles.jobName}>{r.action}</td>
          <td>{r.performed_by || 'system'}</td>
          <td className={styles.recipientsCell}>{r.reason || ''}</td>
          <td>{formatDate(r.created_at)}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const AuthTable = ({ rows }) => (
  <table className={styles.table}>
    <thead>
      <tr>
        <th>When</th>
        <th>User</th>
        <th>Organization</th>
        <th>Route</th>
        <th>Mode</th>
        <th>Verdict</th>
        <th>Reason</th>
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <tr key={r.id}>
          <td>{formatDate(r.created_at)}</td>
          <td className={styles.monoCell}>{r.user_sub || '—'}</td>
          <td>{r.organization || '—'}</td>
          <td className={styles.monoCell}>
            {r.method} {r.route}
          </td>
          <td>{r.mode || '—'}</td>
          <td>
            <StatusChip
              label={r.verdict}
              color={STATUS_COLORS[r.verdict] || 'neutral'}
              size='small'
            />
          </td>
          <td>{r.reason || ''}</td>
        </tr>
      ))}
    </tbody>
  </table>
)

const TABLES = {
  jobs: JobsTable,
  autoqa: AutoQaTable,
  emails: EmailsTable,
  webhooks: WebhooksTable,
  audit: AuditTable,
  auth: AuthTable
}

const AdminBillingLogs = () => {
  const [activeTab, setActiveTab] = useState('jobs')
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [offset, setOffset] = useState(0)
  const [hasMore, setHasMore] = useState(false)

  const fetchLogs = useCallback(async (type, pageOffset) => {
    try {
      setLoading(true)
      setError(null)
      const url = `${apiEndpoints.adminLogs}?type=${type}&limit=${PAGE_SIZE}&offset=${pageOffset}`
      const response = await authenticatedFetchJson(url)
      const data = response?.data || []
      setRows(data)
      setHasMore(data.length === PAGE_SIZE)
    } catch (err) {
      logger.error('Failed to load logs:', err)
      setError(err.message || 'Failed to load logs')
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchLogs(activeTab, offset)
  }, [activeTab, offset, fetchLogs])

  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId)
    setOffset(0)
  }, [])

  const activeTabMeta = TABS.find((t) => t.id === activeTab)
  const Table = TABLES[activeTab]

  return (
    <PageLayout>
      <PageHeader
        title='Logs'
        subtitle='Operational history — jobs, emails, webhooks, audit trail, auth decisions'
      />

      <div className={styles.tabBar} role='tablist'>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type='button'
            role='tab'
            aria-selected={activeTab === tab.id}
            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
            onClick={() => handleTabChange(tab.id)}
            data-testid={`tab-${tab.id}`}
          >
            <FontAwesomeIcon icon={tab.icon} className={styles.tabIcon} />
            <span>{tab.label}</span>
          </button>
        ))}
      </div>

      <div className={styles.retentionNote}>
        Retention: <strong>{activeTabMeta?.retention}</strong>
      </div>

      {loading ? (
        <LoadingSpinner message='Loading logs...' />
      ) : error ? (
        <div className={styles.errorState}>
          <p>{error}</p>
        </div>
      ) : rows.length === 0 ? (
        <div className={styles.emptyState}>No {activeTabMeta?.label.toLowerCase()} logs yet</div>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <Table rows={rows} />
          </div>
          <div className={styles.pagination}>
            <button
              type='button'
              className={styles.pageButton}
              disabled={offset === 0}
              onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
            >
              Previous
            </button>
            <span className={styles.pageInfo}>
              Showing {offset + 1}–{offset + rows.length}
            </span>
            <button
              type='button'
              className={styles.pageButton}
              disabled={!hasMore}
              onClick={() => setOffset((o) => o + PAGE_SIZE)}
            >
              Next
            </button>
          </div>
        </>
      )}
    </PageLayout>
  )
}

export default AdminBillingLogs
